import { ValidatedCsvReportResult } from '@screens/ReportCreation/components/CsvImport/types/csvValidation.types';
import { TFunction } from 'i18next';
import { supportedLanguages } from '@store/i18n';
import { parseCsvContent } from '@screens/ReportCreation/components/CsvImport/utils/csvParser.utils';
import { validateReports } from '@screens/ReportCreation/components/CsvImport/utils/validation/csvValidation.utils';
import {
  ReportFormData,
  type ReportSubmissionMode,
} from '@screens/ReportCreation/reportCreation.types';
import {
  type DiagnosticProcedureBodyDto,
  DiagnosticProcedureBodyDtoCategoryOfTest,
  ImportReportDto,
  ImportReportDtoAnimalType,
  ImportReportDtoDomesticAnimalAffected,
  ImportReportDtoHumanAffected,
  ImportReportDtoStatus,
  ImportReportDtoSurveillanceType,
  ImportReportDtoWildType,
  type ReferentialsResponseDto,
} from '@api/WhisApi/generated';
import {
  buildDiagnosticProcedures,
  getDefinedValue,
  isNonEmptyString,
  mapKnownCause,
  mapYesNoUnknown,
  resolveSpeciesValue,
  toDateString,
  toEnumValue,
  toNumber,
} from '@screens/ReportCreation/utils/reportPayloadMapping.utils';
import { transformFlatObjectToTyped } from '@utils/transformFlatObjectToTyped';
import {
  CsvContent,
  ImportReportDataKeys,
  ReportCsvParseResult,
  ReportCsvStructure,
} from '@screens/ReportCreation/components/CsvImport/types/csvParser.types';
import { getCsvColumns } from '@screens/ReportCreation/components/CsvImport/utils/csvTemplate.utils';

const findReportCsvStructure = (
  fileHeaders: string[],
  t: TFunction,
): ReportCsvStructure | null => {
  for (const lang of supportedLanguages) {
    const columns = getCsvColumns(t, lang);
    const headers = columns.map((col) => col.header);

    if (fileHeaders.every((header) => headers.includes(header))) {
      return {
        lang,
        columns,
      };
    }
  }
  return null;
};

export const parseReportCsv = (
  parsedCsv: CsvContent<Record<string, string>>,
  t: TFunction,
): ReportCsvParseResult => {
  if (!parsedCsv.headers) {
    throw 'reportCreation.csvImportValidation.errors.unableToReadFile';
  }
  const csvStructure = findReportCsvStructure(parsedCsv.headers, t);

  if (!csvStructure) {
    throw 'reportCreation.csvImportValidation.errors.missingColumn';
  }
  return {
    lang: csvStructure.lang,
    reports: parsedCsv.content.map((row) => {
      return csvStructure.columns.reduce(
        (report, column) => {
          report[column.key] = row[column.header];
          return report as ReportFormData;
        },
        {} as Record<ImportReportDataKeys, string>,
      );
    }),
  };
};

export const buildImportReportDto = (
  values: ReportFormData,
  mode: ReportSubmissionMode,
): ImportReportDto => {
  const knownCause = mapKnownCause(values.knowTheCause);
  const includeEtiology = knownCause === true;
  const diagnosticEnabled = values.diagnosticProcedure === 'yes';
  const diagnosticProcedures = diagnosticEnabled
    ? buildDiagnosticProcedures(
        values.diagnosticProcedures,
        DiagnosticProcedureBodyDtoCategoryOfTest,
        (procedure): DiagnosticProcedureBodyDto => procedure,
      )
    : undefined;
  const specie = resolveSpeciesValue(values);
  const latitude = toNumber(values.location?.latitude);
  const longitude = toNumber(values.location?.longitude);
  const animalType = toEnumValue(ImportReportDtoAnimalType, values.animalType);
  const wildType = toEnumValue(ImportReportDtoWildType, values.wildtype);
  const detectionDate = toDateString(values.dateOfDetection);
  const surveillanceType = toEnumValue(
    ImportReportDtoSurveillanceType,
    values.surveillanceType,
  );
  const numberOfDeaths = toNumber(values.numberOfDeaths);
  const numberOfSicks = toNumber(values.numberOfSick);
  const populationSize = toNumber(values.populationSize);
  const humanAffected = mapYesNoUnknown(
    ImportReportDtoHumanAffected,
    values.humanAffected,
  );
  const domesticAnimalAffected = mapYesNoUnknown(
    ImportReportDtoDomesticAnimalAffected,
    values.domesticAnimalAffected,
  );

  return {
    status:
      mode === 'draft'
        ? ImportReportDtoStatus.draft
        : ImportReportDtoStatus.published,
    ...(animalType && { animalType }),
    ...(specie && { specie }),
    ...(wildType && { wildType }),
    ...getDefinedValue('latitude', latitude),
    ...getDefinedValue('longitude', longitude),
    ...(typeof values.inaccurateLocation === 'boolean' && {
      inaccurateLocation: values.inaccurateLocation,
    }),
    ...(detectionDate && { detectionDate }),
    ...(surveillanceType && { surveillanceType }),
    ...(isNonEmptyString(values.description) && {
      description: values.description,
    }),
    ...getDefinedValue('numberOfDeaths', numberOfDeaths),
    ...getDefinedValue('numberOfSicks', numberOfSicks),
    ...getDefinedValue('populationSize', populationSize),
    ...(humanAffected && { humanAffected }),
    ...(domesticAnimalAffected && { domesticAnimalAffected }),
    ...(knownCause !== undefined && { knownCause }),
    ...(includeEtiology &&
      isNonEmptyString(values.etiologyType) && {
        etiologyTypeCode: values.etiologyType,
      }),
    ...(includeEtiology &&
      isNonEmptyString(values.etiologyAgent) && {
        etiologicalAgentCode: values.etiologyAgent,
      }),
    ...(diagnosticProcedures && { diagnosticProcedures }),
  };
};

export const mapAndValidateReportCsv = (
  csvContent: string,
  t: TFunction,
  referentials?: ReferentialsResponseDto,
): ValidatedCsvReportResult => {
  const topErrorMessage = {
    rowNumber: 1,
    columnHeader: t('reportCreation.csvImportValidation.file'),
    message: t('reportCreation.csvImportValidation.errors.noDataRows'),
  };

  try {
    const parsedCsv = parseCsvContent<Record<string, string>>(csvContent);
    const { lang: fileLang, reports } = parseReportCsv(parsedCsv, t);
    const validationResult = validateReports(
      reports,
      t,
      fileLang,
      referentials,
    );

    return {
      totalRows: validationResult.totalRows,
      validRows:
        validationResult.parsedRows.length - validationResult.invalidRows,
      invalidRows: validationResult.invalidRows,
      parsedRows: validationResult.parsedRows.map((flatReport) =>
        transformFlatObjectToTyped<ReportFormData>(flatReport),
      ),
      errors: validationResult.errors,
    };
  } catch (e: unknown) {
    console.error('Error while validation csv', e);
    if (typeof e === 'string' && t(e)) {
      topErrorMessage.message = t(e);
    }
    return {
      totalRows: 0,
      invalidRows: 0,
      validRows: 0,
      parsedRows: [],
      errors: [topErrorMessage],
    };
  }
};
